import { useEffect, useRef, useState } from 'react';
import { createTimeOffCalendarClient } from './createTimeOffCalendarClient';
import { TIME_OFF_CALENDAR_REALTIME_DEBOUNCE_MS } from './timeOffCalendarRealtimeTypes';

export interface UseTimeOffCalendarRealtimeOptions {
  enabled?: boolean;
  /** Skip pushes while a popup is open — same rule as background polling. */
  popupOpen?: boolean;
  onCalendarChanged: () => void | Promise<void>;
}

/**
 * Subscribes to time-off calendar SignalR pushes and debounces refresh callbacks.
 * Returns whether the hub connection is currently up (drives slower fallback polling).
 */
export function useTimeOffCalendarRealtime(options: UseTimeOffCalendarRealtimeOptions): { connected: boolean } {
  const { enabled = true, popupOpen = false, onCalendarChanged } = options;
  const [connected, setConnected] = useState(false);

  const refreshRef = useRef(onCalendarChanged);
  refreshRef.current = onCalendarChanged;

  const popupOpenRef = useRef(popupOpen);
  popupOpenRef.current = popupOpen;

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!enabled) {
      setConnected(false);
      return;
    }

    const scheduleRefresh = () => {
      if (popupOpenRef.current) return;
      if (debounceTimerRef.current != null) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        if (popupOpenRef.current) return;
        void refreshRef.current();
      }, TIME_OFF_CALENDAR_REALTIME_DEBOUNCE_MS);
    };

    const client = createTimeOffCalendarClient({
      onCalendarChanged: scheduleRefresh,
      onConnectedChange: setConnected
    });
    void client.start();

    return () => {
      if (debounceTimerRef.current != null) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      void client.stop();
      setConnected(false);
    };
  }, [enabled]);

  return { connected };
}
